import { InputRule } from "@handlewithcare/prosemirror-inputrules";
import { type MarkType } from "prosemirror-model";

// Map of smart quotes to ASCII equivalents
const smartQuoteReplacements: Record<string, string> = {
	"\u201C": '"', // left double quotation mark (")
	"\u201D": '"', // right double quotation mark (")
	"\u2018": "'", // left single quotation mark (')
	"\u2019": "'", // right single quotation mark (')

	// SOME DAY: Unicode smart quotes. The input rules library doesn't turn these on by default, you
	// have to add them. But they might be cool to do at some point.
	//
	// "\u201A": "'", // single low-9 quotation mark (‚)
	// "\u201E": '"', // double low-9 quotation mark („)
	// "\u2039": "'", // single left-pointing angle quotation mark (‹)
	// "\u203A": "'", // single right-pointing angle quotation mark (›)
	// "\u00AB": '"', // left-pointing double angle quotation mark («)
	// "\u00BB": '"', // right-pointing double angle quotation mark (»)
};

export function replaceSmartQuotes(text: string): string {
	return text.replace(
		/[\u201C\u201D\u2018\u2019]/g,
		(match) => smartQuoteReplacements[match] || match
	);
}

export const inlineCodeRule = (markType: MarkType): InputRule => {
	const regexp = /`(?<content>[^`]+)`$/d;

	return new InputRule(regexp, (state, match, start, end) => {
		const tr = state.tr;
		if (!regexp.hasIndices) {
			return null;
		}

		const content = match.groups?.["content"];
		const matchIndices = match.indices?.[0];
		const contentIndices = match.indices?.groups?.["content"];

		if (!content || !contentIndices || !matchIndices) {
			return null;
		}

		const contentStart = start + (contentIndices[0] - matchIndices[0]);
		const contentEnd = contentStart + content.length;

		// Replace smart quotes with ASCII equivalents in the content
		const normalizedContent = replaceSmartQuotes(content);

		// Delete the opening backtick
		tr.delete(start, contentStart);

		// After deleting opening backtick, positions shift
		// The content now starts at 'start' and ends at (contentEnd - 1)
		const shiftedContentEnd = contentEnd - 1;

		// Replace the content if it has smart quotes
		if (normalizedContent !== content) {
			tr.replaceWith(start, shiftedContentEnd, state.schema.text(normalizedContent));
		}

		// After replacement, calculate new end position
		const newEnd = start + normalizedContent.length;

		// Delete the closing backtick (which is now right after the content)
		tr.delete(newEnd, newEnd + 1);

		// Apply the code mark to the content (not including the backticks)
		tr.addMark(start, newEnd, markType.create());
		tr.removeStoredMark(markType);

		return tr;
	});
};
